import {
  Button,
  Card,
  Group,
  Pagination,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { FunctionComponent } from "react";
import StarRating from "./StarRating";

export interface Review {
  id: number;
  user_id: string;
  display_name?: string | null;
  rating: number;
  review_text: string | null;
  created_at: string;
  updated_at: string;
}

interface ReviewListProps {
  /** List of reviews */
  reviews: Review[];
  /** Current user ID (to show edit/delete buttons) */
  currentUserId?: string;
  /** Current page */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when edit is clicked */
  onEdit?: (review: Review) => void;
  /** Callback when delete is clicked */
  onDelete?: (review: Review) => void;
  /** Whether data is loading */
  isLoading?: boolean;
}

export const ReviewList: FunctionComponent<ReviewListProps> = ({
  reviews,
  currentUserId,
  page,
  totalPages,
  onPageChange,
  onEdit,
  onDelete,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Stack gap="md">
        {[...Array(3)].map((_, i) => (
          <Card key={i} withBorder padding="md">
            <Stack gap="xs">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Skeleton height={20} width={120} mb={4} />
                  <Skeleton height={16} width={100} />
                </div>
                <Skeleton height={16} width={80} />
              </Group>
              <Skeleton height={60} />
            </Stack>
          </Card>
        ))}
      </Stack>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <Stack gap="md">
      {reviews.map((review) => (
        <Card key={review.id} withBorder padding="md">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <div>
                <Group gap="xs" mb={4}>
                  <StarRating value={review.rating} showCount={false} />
                  {currentUserId === review.user_id && (
                    <Text size="xs" c="dimmed" fs="italic">
                      (Your Review)
                    </Text>
                  )}
                </Group>
                <Text size="sm" fw={500} c="dimmed">
                  {review.display_name || "Anonymous"}
                </Text>
              </div>
              <Text size="sm" c="dimmed">
                {new Date(review.created_at).toLocaleDateString()}
              </Text>
            </Group>

            {review.review_text && (
              <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                {review.review_text}
              </Text>
            )}

            {currentUserId === review.user_id && (
              <Group gap="xs">
                {onEdit && (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => onEdit(review)}
                  >
                    Edit
                  </Button>
                )}
                {onDelete && (
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    onClick={() => onDelete(review)}
                  >
                    Delete
                  </Button>
                )}
              </Group>
            )}
          </Stack>
        </Card>
      ))}

      {totalPages > 1 && (
        <Pagination total={totalPages} value={page} onChange={onPageChange} />
      )}
    </Stack>
  );
};

export default ReviewList;
